import { SxProps } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
  DTA_MEDICAL_FORM_APPROVED_BY,
  EARLY_REHAB_CRITERIA,
  GENDER,
  ICD_REASON,
  MUSCOSKELETAL_RESILIENCE,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  SPECIAL_REQUIREMENTS,
  TRANSPORTATION_SUBTYPE,
  TRANSPORTATION_TYPE,
  TYPE_OF_REHAB,
} from "core/consts";
import { INFECTIONS_CHARACTER_LIMIT } from "core/schemas/shared";
import { PredicamentState, ValueOf } from "core/types";
import { CheckboxGroupProps } from "ds_legacy/components/CheckboxGroup";
import { CheckboxOptionV2 } from "ds_legacy/components/CheckboxGroupV2";
import RadioGroupV2, { RadioOptionV2 } from "ds_legacy/components/RadioGroupV2";
import { SelectOption } from "ds_legacy/components/SelectInput";
import { RADIO_WHITE_BACKGROUND } from "ds_legacy/materials/colors";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import {
  DatePickerWithLabel,
  SMALL_INPUT_MIN_WIDTH,
  SMALL_INPUT_WIDTH,
  TextAreaWithLabel,
  TextInputWithLabel,
} from "dsl/ecosystems/PatientForms/components";
import { useTranslations } from "translations";
import { ApprovedByType } from "core/types";

export function useGenderOptions() {
  const translations = useTranslations();

  const genderOptions: RadioOptionV2<ValueOf<typeof GENDER>>[] = [
    {
      label: translations.patientForms.medicalForm.sectionOne.genderMale,
      value: GENDER.MALE,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.genderFemale,
      value: GENDER.FEMALE,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.genderDiverse,
      value: GENDER.DIVERSE,
    },
    {
      label: translations.patientForms.drvGeneralForm.sectionTwo.unknown,
      value: GENDER.UNSPECIFIED,
    },
  ];

  return genderOptions;
}

export function useICDReasonOptions() {
  const translations = useTranslations();

  const ICD_REASON_OPTIONS: Record<number, string> = {
    [ICD_REASON.DAMAGE_FROM_THIRD_PARTIES]:
      translations.patientForms.medicalForm.sectionTwo.optionFour,
    [ICD_REASON.MILITARY_SERVICE]:
      translations.patientForms.medicalForm.sectionTwo.optionFive,
    [ICD_REASON.NOT_APPLICABLE]:
      translations.patientForms.medicalForm.sectionTwo.optionOne,
    [ICD_REASON.NOTIFIABLE_DISEASE]:
      translations.patientForms.medicalForm.sectionTwo.optionSix,
    [ICD_REASON.OCCUPATIONAL_DISEASE]:
      translations.patientForms.medicalForm.sectionTwo.optionThree,
    [ICD_REASON.WORK_ACCIDENT]:
      translations.patientForms.medicalForm.sectionTwo.optionTwo,
  };

  const icdReasonOptions: SelectOption[] = Object.entries(
    ICD_REASON_OPTIONS,
  ).map(([key, label]) => ({
    id: key,
    label: label,
    value: key,
  }));
  return icdReasonOptions;
}

export function useComplicationOptions() {
  const translations = useTranslations();

  const complicationOptions = [
    {
      elementName: "complication_cause_maindiagnosis_description",
      label:
        translations.patientForms.medicalForm.sectionTwo.relatedToDiagnosis,
    },
    {
      elementName: "complication_cause_cardiovascular_description",
      label:
        translations.patientForms.medicalForm.sectionTwo.optionCardiovascular,
    },
    {
      elementName: "complication_cause_other_description",
      label:
        translations.patientForms.medicalForm.sectionTwo
          .optionOtherComplication,
    },
  ] as const;

  return complicationOptions;
}

export function useRehabAdmissionOptions() {
  const translations = useTranslations();

  const rehabAdmissionOptions: RadioOptionV2<
    ValueOf<typeof REHAB_ADMISSION>
  >[] = [
    {
      label: translations.patientForms.medicalForm.sectionOne.optionDirect,
      value: REHAB_ADMISSION.DIRECT,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.withinTwoWeeks,
      subForm: (
        <DatePickerWithLabel
          elementName="rehab_admission_within_two_weeks_start_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: SMALL_INPUT_WIDTH,
          }}
          isBold
          label={
            translations.patientForms.medicalForm.sectionOne
              .earliestDatePossible
          }
          withFlatModel
          wrapperSx={{ marginBottom: sizing(2), width: "100%" }}
        />
      ),
      value: REHAB_ADMISSION.WITHIN_TWO_WEEKS,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.outsideTwoWeeks,
      subForm: (
        <>
          <DatePickerWithLabel
            elementName="rehab_admission_outside_two_weeks_start_date"
            inputSx={{
              minWidth: SMALL_INPUT_MIN_WIDTH,
              width: SMALL_INPUT_WIDTH,
            }}
            isBold
            label={
              translations.patientForms.medicalForm.sectionOne
                .earliestDatePossible
            }
            withFlatModel
            wrapperSx={{ padding: padding(0, 0, 1), width: "100%" }}
          />
          <TextAreaWithLabel
            elementName="rehab_admission_outside_two_weeks_explanation"
            isBold
            label={
              translations.patientForms.medicalForm.sectionOne.medicalReasons
            }
            marginOverride={margin(0, 0, 1)}
            withFlatModel
          />
        </>
      ),
      value: REHAB_ADMISSION.OUTSIDE_TWO_WEEKS,
    },
  ];

  return rehabAdmissionOptions;
}

export function useInfectionOptions() {
  const translations = useTranslations();

  const infectionOptions: RadioOptionV2<PredicamentState>[] = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: (
        <TextAreaWithLabel
          characterLimit={INFECTIONS_CHARACTER_LIMIT}
          elementName="infections"
          label={translations.patientForms.medicalForm.sectionTwo.typeOfGerms}
          marginOverride={margin(0, 0, 1)}
          required
          withFlatModel
          wrapperSx={{ padding: padding(0, 0, 0, 4.5) }}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
    {
      label: translations.patientForms.medicalForm.sectionTwo.germsUnknown,
      value: PREDICAMENT_STATE_UNKNOWN,
    },
  ];

  return infectionOptions;
}

export function useMusculoskeletalResilienceOptions() {
  const translations = useTranslations();

  const musculoskeletalResilienceOptions: RadioOptionV2<
    ValueOf<typeof MUSCOSKELETAL_RESILIENCE>
  >[] = [
    {
      label: translations.patientForms.medicalForm.sectionThree.fully,
      value: MUSCOSKELETAL_RESILIENCE.FULLY,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.partially,
      subForm: (
        <TextInputWithLabel
          elementName="reha_capacity_partial"
          endAdornment={translations.abbreviations.kilogram.abbreviation}
          marginOverride={margin(0, 0, 1, 4.5)}
          withFlatModel
        />
      ),
      value: MUSCOSKELETAL_RESILIENCE.PARTIALLY,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.stable,
      value: MUSCOSKELETAL_RESILIENCE.STABLE,
    },
  ];

  return musculoskeletalResilienceOptions;
}

export function useEarlyRehabCriteriaOptions() {
  const translations = useTranslations();
  const earlyRehabCriteriaOptions: CheckboxGroupProps["items"] = [
    {
      id: EARLY_REHAB_CRITERIA.INTENSIVE_CARE_MONITORING,
      label:
        translations.patientForms.medicalForm.sectionThree
          .intensiveCareMonitoring,
    },
    {
      id: EARLY_REHAB_CRITERIA.INTERMITTENT_VENTILATION,
      label:
        translations.patientForms.medicalForm.sectionThree
          .intermittentVentilation,
    },
    {
      id: EARLY_REHAB_CRITERIA.BEHAVIOURAL_DISORDER,
      label:
        translations.patientForms.medicalForm.sectionThree.behaviouralDisorder,
    },
    {
      id: EARLY_REHAB_CRITERIA.COMMUNICATION_DISORDER,
      label:
        translations.patientForms.medicalForm.sectionThree
          .communicationDisorder,
    },
    {
      id: EARLY_REHAB_CRITERIA.TRACHEOSTOMA,
      label: translations.patientForms.medicalForm.sectionThree.tracheostoma,
    },
    {
      id: EARLY_REHAB_CRITERIA.ORIENTATION_DISORDER,
      label:
        translations.patientForms.medicalForm.sectionThree.orientationDisorder,
    },
    {
      id: EARLY_REHAB_CRITERIA.SWALLOWING_DISORDER,
      label:
        translations.patientForms.medicalForm.sectionThree.swallowingDisorder,
    },
  ];

  return earlyRehabCriteriaOptions;
}

export function useRehabFocusOptions() {
  const translations = useTranslations();

  const rehabFocusOptions: RadioOptionV2<ValueOf<typeof REHAB_FOCUS>>[] = [
    {
      label: translations.patientForms.medicalForm.sectionFive.heart,
      value: REHAB_FOCUS.HEART,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.neurologicalDisease,
      value: REHAB_FOCUS.NEUROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bloodVessels,
      value: REHAB_FOCUS.BLOOD_VESSELS,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.oncologicalDiseases,
      value: REHAB_FOCUS.ONCOLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.inflammatoryDiseases,
      value: REHAB_FOCUS.INFLAMMATORY_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .gynaecologicalDiseases,
      value: REHAB_FOCUS.GYNAECOLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.musculoskeletalSystem,
      value: REHAB_FOCUS.MUSCOLOSKELETAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.skinDiseases,
      value: REHAB_FOCUS.SKIN_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .gastroenterologicalDiseases,
      value: REHAB_FOCUS.GASTROENTEROLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .psychosomaticDisorders,
      value: REHAB_FOCUS.PSYCHOSOMATIC_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.endocrineDiseases,
      value: REHAB_FOCUS.ENDOCRINE_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.mentalIllnesses,
      value: REHAB_FOCUS.MENTAL_ILLNESS,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.respiratoryDiseases,
      value: REHAB_FOCUS.RESPIRATORY_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.addictiveDisorders,
      value: REHAB_FOCUS.ADDICTIVE_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.nephrologicalDiseases,
      value: REHAB_FOCUS.NEPHROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bloodDiseases,
      value: REHAB_FOCUS.BLOOD_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.urologicalDiseases,
      value: REHAB_FOCUS.UROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.geriatrics,
      value: REHAB_FOCUS.GERIATRICS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.others,
      subForm: (
        <TextAreaWithLabel
          elementName="rehab_focus_other_description"
          label={
            translations.patientForms.medicalForm.sectionFive
              .otherTextfieldLabel
          }
          marginOverride={margin(0)}
          withFlatModel
        />
      ),
      value: REHAB_FOCUS.OTHER,
    },
  ];

  return rehabFocusOptions;
}

export function useRehabTypeOptions() {
  const translations = useTranslations();

  const rehabTypeOptions: RadioOptionV2<ValueOf<typeof TYPE_OF_REHAB>>[] = [
    {
      label: translations.patientForms.medicalForm.sectionFive.mobile,
      value: TYPE_OF_REHAB.AMBULANT,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.mobileOutpatient,
      value: TYPE_OF_REHAB.AMBULANT_MOBIL,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.static,
      value: TYPE_OF_REHAB.STATIONARY,
    },
  ];

  return rehabTypeOptions;
}

export function useRehabTypeDirectiveOptions() {
  const translations = useTranslations();

  const rehabTypeDirectiveOptions: CheckboxOptionV2[] = [
    {
      label:
        translations.patientForms.medicalForm.sectionFive.myocardialInfarction,
      value: REHAB_DIRECTIVE_TYPE.MYOCARDIAL_INFARCTION,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bypass,
      value: REHAB_DIRECTIVE_TYPE.BYPASS_WITH_VALVE_REPLACEMENT,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.heartFailure,
      value: REHAB_DIRECTIVE_TYPE.HEART_FAILURE_NYHA_II_OR_HIGHER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .endoprostheticTreatment,
      value: REHAB_DIRECTIVE_TYPE.ENDOPROSTHETIC_KNEE_OR_HIP,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.backPain,
      value: REHAB_DIRECTIVE_TYPE.CHRONIC_BACK_PAIN,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.spinalCanal,
      value: REHAB_DIRECTIVE_TYPE.SPINAL_STENOSIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.amputation,
      value: REHAB_DIRECTIVE_TYPE.MAJOR_LEG_AMPUTATION,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.respiratoryDisease,
      value: REHAB_DIRECTIVE_TYPE.RESPIRATORY_DISEASES,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.neurologicalDiseases,
      value: REHAB_DIRECTIVE_TYPE.NEUROLOGICAL_DISEASE_PHASE_D,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.oncologicalDisease,
      value: REHAB_DIRECTIVE_TYPE.ONCOLOGICAL_DISEASES,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.transplants,
      value: REHAB_DIRECTIVE_TYPE.ORGAN_TRANSPLANTS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.geriatricRehab,
      value: REHAB_DIRECTIVE_TYPE.GERIATRIC_REHAB,
    },
  ];

  return rehabTypeDirectiveOptions;
}

export const SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS: Partial<
  Record<ValueOf<typeof SPECIAL_REQUIREMENTS>, string>
> = {
  [SPECIAL_REQUIREMENTS.DECUBITUS]:
    "special_requirements_decubitus_description",
  [SPECIAL_REQUIREMENTS.DIALYSIS]: "special_requirements_dialysis_description",
  [SPECIAL_REQUIREMENTS.OTHER]: "special_requirements_other_description",
  [SPECIAL_REQUIREMENTS.RELATIVES]:
    "special_requirements_relatives_description",
  [SPECIAL_REQUIREMENTS.WEIGHT_BED]:
    "special_requirements_weight_bed_description",
};

export function useSpecialRehabRequirementOptions() {
  const translations = useTranslations();

  const specialRehabRequirementsItems: CheckboxOptionV2[] = [
    {
      label: translations.patientForms.medicalForm.sectionSix.chemotherapy,
      value: SPECIAL_REQUIREMENTS.CHEMOTHERAPY,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionSix.immunosuppressants,
      value: SPECIAL_REQUIREMENTS.IMMUNOSUPPRESSANTS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.catheter,
      value: SPECIAL_REQUIREMENTS.CATHETER,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.peg,
      value: SPECIAL_REQUIREMENTS.PEG,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.isolation,
      value: SPECIAL_REQUIREMENTS.ISOLATION,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.ventilation,
      value: SPECIAL_REQUIREMENTS.VENTILATION,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.tracheostoma,
      value: SPECIAL_REQUIREMENTS.TRACHEOSTOMA,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.weaning,
      value: SPECIAL_REQUIREMENTS.WEANING,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.prosthesis,
      value: SPECIAL_REQUIREMENTS.PROSTHESIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.overlengthBed,
      value: SPECIAL_REQUIREMENTS.OVERLENGTH_BED,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.obesity,
      value: SPECIAL_REQUIREMENTS.OBESITY,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.impairment,
      value: SPECIAL_REQUIREMENTS.IMPAIRMENT,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.relatives,
      subForm: (
        <TextAreaWithLabel
          elementName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.RELATIVES
            ] as string
          }
          label={
            translations.patientForms.medicalForm.sectionFive
              .otherTextfieldLabel
          }
          marginOverride={margin(0)}
          withFlatModel
        />
      ),
      value: SPECIAL_REQUIREMENTS.RELATIVES,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.dialysis,
      subForm: (
        <TextAreaWithLabel
          elementName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.DIALYSIS
            ] as string
          }
          label={
            translations.patientForms.medicalForm.sectionFive
              .otherTextfieldLabel
          }
          marginOverride={margin(0)}
          withFlatModel
        />
      ),
      value: SPECIAL_REQUIREMENTS.DIALYSIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.decubitus,
      subForm: (
        <TextAreaWithLabel
          elementName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.DECUBITUS
            ] as string
          }
          label={
            translations.patientForms.medicalForm.sectionFive
              .otherTextfieldLabel
          }
          marginOverride={margin(0)}
          withFlatModel
        />
      ),
      value: SPECIAL_REQUIREMENTS.DECUBITUS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.weightBed,
      subForm: (
        <TextInputWithLabel
          elementName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.WEIGHT_BED
            ] as string
          }
          endAdornment={translations.abbreviations.kilogram.abbreviation}
          marginOverride={margin(0)}
          withFlatModel
        />
      ),
      value: SPECIAL_REQUIREMENTS.WEIGHT_BED,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.other,
      subForm: (
        <TextAreaWithLabel
          elementName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.OTHER
            ] as string
          }
          label={
            translations.patientForms.medicalForm.sectionFive
              .otherTextfieldLabel
          }
          marginOverride={margin(0)}
          withFlatModel
        />
      ),
      value: SPECIAL_REQUIREMENTS.OTHER,
    },
  ];

  return specialRehabRequirementsItems;
}

export function useTransportOptions() {
  const translations = useTranslations();

  const taxiOptions: RadioOptionV2<ValueOf<typeof TRANSPORTATION_SUBTYPE>>[] = [
    {
      label: translations.patientForms.medicalForm.sectionSix.wheelChair,
      value: TRANSPORTATION_SUBTYPE.WHEELCHAIR,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.stretcher,
      value: TRANSPORTATION_SUBTYPE.STRETCHER,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.lyingDown,
      value: TRANSPORTATION_SUBTYPE.LYING_DOWN,
    },
  ];

  const transportOptions: RadioOptionV2<ValueOf<typeof TRANSPORTATION_TYPE>>[] =
    [
      {
        label:
          translations.patientForms.medicalForm.sectionSix
            .optionPublicTransport,
        value: TRANSPORTATION_TYPE.PUBLIC,
      },
      {
        label: translations.patientForms.medicalForm.sectionSix.car,
        value: TRANSPORTATION_TYPE.CAR,
      },
      {
        label: translations.patientForms.medicalForm.sectionSix.taxi,
        subForm: (
          <RadioGroupV2
            elementName="transport_type_taxi_specification"
            formControlLabelSx={{
              marginLeft: sizing(4),
              paddingBottom: sizing(0.5),
            }}
            formLabelSx={visuallyHidden as SxProps}
            isRequired
            label={
              translations.patientForms.medicalForm.sectionSix.taxiOptionLabel
            }
            options={taxiOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            withFlatModel
          />
        ),
        value: TRANSPORTATION_TYPE.TAXI,
      },
      {
        label: translations.patientForms.medicalForm.sectionSix.ambulance,
        subForm: (
          <div className="flex flex-col space-y-2">
            <RadioGroupV2
              elementName="transport_type_ambulance_specification"
              formControlLabelSx={{
                marginLeft: sizing(4),
                paddingBottom: sizing(0.5),
              }}
              formLabelSx={visuallyHidden as SxProps}
              isRequired
              label={
                translations.patientForms.medicalForm.sectionSix.taxiOptionLabel
              }
              options={taxiOptions}
              radioSx={RADIO_WHITE_BACKGROUND}
              withFlatModel
            />
            <TextAreaWithLabel
              elementName="transport_type_ambulance_description"
              label={
                translations.patientForms.medicalForm.sectionSix
                  .ambulanceDescriptionLabel
              }
              marginOverride={margin(0)}
              withFlatModel
            />
          </div>
        ),
        value: TRANSPORTATION_TYPE.AMBULANCE,
      },
    ];

  return transportOptions;
}

export function useDoctorAuthorizationOptions() {
  const translations = useTranslations();

  const options: RadioOptionV2<ApprovedByType>[] = [
    {
      label:
        translations.insuranceApp.eligibilityTab.approvalFlow
          .authorisationOptionOne,
      value: DTA_MEDICAL_FORM_APPROVED_BY.DOCTOR,
    },
    {
      label:
        translations.insuranceApp.eligibilityTab.approvalFlow
          .authorisationQuestionTwo,
      value: DTA_MEDICAL_FORM_APPROVED_BY.SOCIAL_WORKER,
    },
  ];

  return options;
}
